import { useMemo, useState, type ReactNode } from "react";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, StatusBadge, useToast } from "./components/admin-ui";
import { adminUsers, recentActivity, type AdminUser, type ActivityItem } from "@/mocks/admin";
import { newsArticles } from "@/mocks/news";
import { videos } from "@/mocks/videos";

function NeedsBackend({ children }: { children: ReactNode }) {
  return (
    <div className="mt-4 rounded-md border border-accent-200 bg-accent-50 p-3 text-xs text-accent-700 flex items-start gap-2">
      <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
        <i className="ri-information-line text-sm" />
      </span>
      <span>{children}</span>
    </div>
  );
}

// ---------------- Users & Roles ----------------
export function UsersAdminPage() {
  const { items, update } = useCollection<AdminUser>("ltv_users_v1", adminUsers);
  const { show } = useToast();

  return (
    <div className="space-y-6">
      <PageHeader title="Users & Roles" subtitle="View staff accounts and roles." />
      <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
        {items.map((u) => (
          <div key={u.id} className="flex items-center gap-4 px-4 py-3.5">
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold shrink-0">
              {u.initials}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground-950">{u.name}</p>
              <p className="text-xs text-foreground-500">{u.email} · {u.department}</p>
            </div>
            <span className="text-xs font-medium text-foreground-700">{u.role}</span>
            <button
              type="button"
              onClick={() => {
                update(u.id, { status: u.status === "active" ? "locked" : "active" });
                show(u.status === "active" ? "Account locked." : "Account unlocked.");
              }}
              className="px-2.5 py-1.5 text-xs font-medium rounded-md border whitespace-nowrap"
            >
              <StatusBadge tone={u.status === "active" ? "published" : "archived"}>{u.status}</StatusBadge>
            </button>
          </div>
        ))}
      </div>
      <NeedsBackend>
        Role-based permissions and real account management (password hashing, 2FA, lockout) require a connected
        backend. Connect Readdy Backend or SaaS Supabase to enforce these server-side.
      </NeedsBackend>
    </div>
  );
}

// ---------------- Security ----------------
export function SecurityAdminPage() {
  return (
    <div className="space-y-6">
      <PageHeader title="Security" subtitle="Security controls and monitoring." />
      <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
        {[
          { label: "Two-factor authentication", hint: "Require a second factor for admin sign-in.", icon: "ri-smartphone-line" },
          { label: "Session timeout", hint: "Automatically sign out inactive sessions.", icon: "ri-timer-line" },
          { label: "Login protection", hint: "Rate limiting and failed-login lockout.", icon: "ri-shield-line" },
          { label: "Security alerts", hint: "Notify on suspicious activity.", icon: "ri-alert-line" },
        ].map((s) => (
          <div key={s.label} className="flex items-center gap-4 px-5 py-4">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-600">
              <i className={`${s.icon} text-base`} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground-900">{s.label}</p>
              <p className="text-xs text-foreground-500">{s.hint}</p>
            </div>
            <span className="text-xs text-foreground-400">Requires backend</span>
          </div>
        ))}
      </div>
      <NeedsBackend>
        Security controls shown here are informational. Enforce real authentication, authorization and session
        security by connecting Readdy Backend or SaaS Supabase.
      </NeedsBackend>
    </div>
  );
}

// ---------------- Activity Logs ----------------
export function ActivityAdminPage() {
  const [filter, setFilter] = useState("");

  const logs: ActivityItem[] = recentActivity;
  const filtered = useMemo(() => (filter ? logs.filter((l) => l.type === filter) : logs), [logs, filter]);

  const iconFor = (t: ActivityItem["type"]) => {
    switch (t) {
      case "news": return "ri-newspaper-line";
      case "video": return "ri-movie-line";
      case "live": return "ri-live-line";
      case "program": return "ri-tv-line";
      case "settings": return "ri-settings-3-line";
      default: return "ri-user-line";
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Activity Logs"
        subtitle="Recent content and system activity."
        actions={
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-background-200 rounded-md text-sm"
          >
            <option value="">All activity</option>
            <option value="news">News</option>
            <option value="video">Videos</option>
            <option value="live">Live TV</option>
            <option value="program">Programs</option>
            <option value="settings">Settings</option>
            <option value="user">Users</option>
          </select>
        }
      />
      <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
        {filtered.map((l) => (
          <div key={l.id} className="flex items-start gap-3 px-5 py-3.5">
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 shrink-0">
              <i className={`${iconFor(l.type)} text-sm`} />
            </span>
            <div className="min-w-0">
              <p className="text-sm text-foreground-800">
                <span className="font-medium text-foreground-950">{l.user}</span> {l.action}{" "}
                <span className="font-medium">{l.target}</span>
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">{l.time}</p>
            </div>
          </div>
        ))}
      </div>
      <NeedsBackend>
        These are demo logs. Full audit trails with protected retention are recorded server-side once a backend is
        connected.
      </NeedsBackend>
    </div>
  );
}

// ---------------- System Status ----------------
export function SystemStatusAdminPage() {
  const services = [
    { label: "Website", status: "Operational" },
    { label: "Database", status: "Not connected" },
    { label: "Storage", status: "Not connected" },
    { label: "Live Stream", status: "Offline" },
    { label: "Email", status: "Not configured" },
    { label: "APIs", status: "Not connected" },
    { label: "Media Processing", status: "Not configured" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="System Status" subtitle="Service health overview." />
      <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
        {services.map((s) => {
          const ok = s.status === "Operational";
          const neutral = s.status === "Offline";
          return (
            <div key={s.label} className="flex items-center gap-4 px-5 py-4">
              <span
                className={`w-2.5 h-2.5 rounded-full ${ok ? "bg-green-500" : neutral ? "bg-accent-500" : "bg-yellow-500"}`}
              />
              <span className="text-sm font-medium text-foreground-900 flex-1">{s.label}</span>
              <StatusBadge tone={ok ? "published" : neutral ? "archived" : "warning"}>{s.status}</StatusBadge>
            </div>
          );
        })}
      </div>
      <NeedsBackend>
        Real service monitoring requires a connected backend. The website itself is running; database, storage and
        email show as not connected until you link them.
      </NeedsBackend>
    </div>
  );
}

// ---------------- Analytics ----------------
export function AnalyticsAdminPage() {
  return (
    <div className="space-y-6">
      <PageHeader title="Analytics" subtitle="Website and content performance." />
      <div className="bg-white rounded-lg border border-background-200 p-10 text-center">
        <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-bar-chart-line text-xl" />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-foreground-900">No analytics data yet</h2>
        <p className="mt-2 text-sm text-foreground-600 max-w-md mx-auto">
          L+TV does not invent analytics. Connect Google Analytics or a backend to start collecting real visitor,
          page-view and video metrics.
        </p>
      </div>
      <NeedsBackend>
        Connect Google Analytics through the integration panel, or a backend for server-side analytics. Only real data
        will be displayed here.
      </NeedsBackend>
    </div>
  );
}

// ---------------- Media Library ----------------
export function MediaLibraryAdminPage() {
  const media = [
    ...newsArticles.map((a) => ({ id: `news-${a.id}`, name: a.headline.slice(0, 40), type: "Image", url: a.image, usedIn: "News" })),
    ...videos.map((v) => ({ id: `video-${v.id}`, name: v.title.slice(0, 40), type: "Thumbnail", url: v.thumbnail, usedIn: "Videos" })),
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Media Library" subtitle="Images, videos and assets used across the site." />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {media.map((m) => (
          <div key={m.id} className="bg-white rounded-lg border border-background-200 overflow-hidden">
            <img src={m.url} alt={m.name} className="w-full h-24 object-cover" />
            <div className="p-3">
              <p className="text-xs font-medium text-foreground-900 truncate">{m.name}</p>
              <p className="text-[11px] text-foreground-500 mt-0.5">{m.type} · {m.usedIn}</p>
            </div>
          </div>
        ))}
      </div>
      <NeedsBackend>
        These are the images referenced by demo content. Real uploads, storage and file management require connected
        storage (Readdy Backend or SaaS Supabase).
      </NeedsBackend>
    </div>
  );
}

// ---------------- Homepage ----------------
const homepageSections = [
  { id: "hero", label: "Hero", hint: "Featured story and logo banner" },
  { id: "breaking", label: "Breaking News Ticker", hint: "Scrolling red news ticker" },
  { id: "live", label: "Live Now", hint: "L+TV LIVE player section" },
  { id: "news", label: "Latest News", hint: "News card grid" },
  { id: "programs", label: "Featured Programs", hint: "Program highlights" },
  { id: "videos", label: "Latest Videos", hint: "Video thumbnails" },
  { id: "social", label: "Social Media", hint: "Social links section" },
  { id: "newsletter", label: "Newsletter", hint: "Stay Connected signup" },
];

interface HomepageSection { id: string; enabled: boolean; }

export function HomepageAdminPage() {
  const { items, update } = useCollection<HomepageSection>(
    "ltv_homepage_v1",
    homepageSections.map((s) => ({ id: s.id, enabled: true })),
  );
  const { show } = useToast();

  return (
    <div className="space-y-6">
      <PageHeader title="Homepage" subtitle="Toggle and arrange homepage sections." />
      <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
        {homepageSections.map((s) => {
          const rec = items.find((i) => i.id === s.id);
          const enabled = rec?.enabled ?? true;
          return (
            <div key={s.id} className="flex items-center gap-4 px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-900">{s.label}</p>
                <p className="text-xs text-foreground-500">{s.hint}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                onClick={() => {
                  update(s.id, { enabled: !enabled });
                  show(`${s.label} ${!enabled ? "enabled" : "disabled"}.`);
                }}
                className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? "bg-primary-500" : "bg-background-300"}`}
              >
                <span
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                    enabled ? "translate-x-[22px]" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Help ----------------
export function HelpAdminPage() {
  const guides = [
    { title: "Creating News", icon: "ri-newspaper-line", text: "Open Newsroom → New Article, write your content, then save or publish." },
    { title: "Managing Live TV", icon: "ri-live-line", text: "Open Live TV, choose a source, enter the stream URL and set the current program." },
    { title: "Uploading Videos", icon: "ri-movie-line", text: "Open Videos → New Video and enter a hosted video URL and thumbnail." },
    { title: "Scheduling Programs", icon: "ri-calendar-check-line", text: "Open TV Schedule → New Schedule Item to add a program to the guide." },
    { title: "Managing Users", icon: "ri-team-line", text: "Open Users & Roles to view staff and their assigned roles." },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Help Center" subtitle="Guides for managing L+TV content." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {guides.map((g) => (
          <div key={g.title} className="bg-white rounded-lg border border-background-200 p-5">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
              <i className={`${g.icon} text-lg`} />
            </span>
            <h3 className="mt-3 text-sm font-semibold text-foreground-900">{g.title}</h3>
            <p className="mt-1 text-xs text-foreground-600">{g.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}